import { captureCharts } from './chartCapture';
import { extractTables } from './reportTables';
import { fileSlug, parseDisplayedNumber, sheetName, toCsv } from './tabular';

export class ExportError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

// Chrome and Firefox refuse to allocate canvases taller than ~32k px.
const MAX_CANVAS_HEIGHT = 30000;
const PDF_MARGIN_PT = 24;
const HEADER_FONT = '14px Roboto, Helvetica, Arial, sans-serif';
const HEADER_LINE_PX = 22;
const MAX_COLUMN_WIDTH = 60;

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

const indexedName = (title, index, ext) =>
  `${String(index + 1).padStart(2, '0')}_${fileSlug(title || `chart_${index + 1}`)}.${ext}`;

const zipAndDownload = async (entries, fileName) => {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  entries.forEach(({ name, data }) => zip.file(name, data));
  downloadBlob(await zip.generateAsync({ type: 'blob' }), fileName);
};

const requireTables = (doc) => {
  const tables = extractTables(doc);
  if (tables.length === 0) throw new ExportError('noTables');
  return tables;
};

const requireCharts = async (doc) => {
  const charts = await captureCharts(doc);
  if (charts.length === 0) throw new ExportError('noCharts');
  return charts;
};

export const exportCsv = async (doc, baseName) => {
  const tables = requireTables(doc);
  if (tables.length === 1) {
    downloadBlob(new Blob([toCsv(tables[0].rows)], { type: 'text/csv;charset=utf-8' }), `${baseName}.csv`);
    return;
  }
  await zipAndDownload(
    tables.map((t, i) => ({ name: indexedName(t.title || `table_${i + 1}`, i, 'csv'), data: toCsv(t.rows) })),
    `${baseName}_csv.zip`,
  );
};

export const exportXlsx = async (doc, baseName) => {
  const tables = requireTables(doc);
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const used = new Set();
  tables.forEach((table, index) => {
    const sheet = workbook.addWorksheet(sheetName(table.title, index, used));
    const widths = [];
    table.rows.forEach((cells, r) => {
      const isHeader = r < table.headerRowCount;
      const row = sheet.addRow(
        cells.map((text) => (isHeader ? text : parseDisplayedNumber(text)?.value ?? text)),
      );
      cells.forEach((text, c) => {
        widths[c] = Math.max(widths[c] || 8, Math.min(MAX_COLUMN_WIDTH, text.length + 2));
        if (!isHeader && parseDisplayedNumber(text)?.percent) {
          row.getCell(c + 1).numFmt = '0.00%';
        }
      });
      if (isHeader) row.font = { bold: true };
    });
    widths.forEach((w, c) => {
      sheet.getColumn(c + 1).width = w;
    });
    if (table.headerRowCount > 0) {
      sheet.views = [{ state: 'frozen', ySplit: table.headerRowCount }];
    }
  });
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${baseName}.xlsx`,
  );
};

export const exportChartsPng = async (doc, baseName) => {
  const charts = await requireCharts(doc);
  if (charts.length === 1) {
    downloadBlob(await dataUrlToBlob(charts[0].dataUrl), `${baseName}.png`);
    return;
  }
  const entries = await Promise.all(
    charts.map(async (c, i) => ({ name: indexedName(c.title, i, 'png'), data: await dataUrlToBlob(c.dataUrl) })),
  );
  await zipAndDownload(entries, `${baseName}_charts.zip`);
};

const SLIDE = { width: 10, height: 5.625, margin: 0.4, titleHeight: 0.6 };

export const exportChartsPptx = async (doc, baseName, { title } = {}) => {
  const charts = await requireCharts(doc);
  const { default: PptxGenJS } = await import('pptxgenjs');
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_16x9';
  if (title) pptx.title = title;
  charts.forEach((chart, i) => {
    const slide = pptx.addSlide();
    slide.addText(chart.title || title || `${i + 1}`, {
      x: SLIDE.margin,
      y: 0.2,
      w: SLIDE.width - 2 * SLIDE.margin,
      h: SLIDE.titleHeight,
      fontSize: 20,
      bold: true,
    });
    const boxTop = 0.2 + SLIDE.titleHeight + 0.1;
    const boxW = SLIDE.width - 2 * SLIDE.margin;
    const boxH = SLIDE.height - boxTop - SLIDE.margin;
    const scale = Math.min(boxW / chart.width, boxH / chart.height);
    const w = chart.width * scale;
    const h = chart.height * scale;
    slide.addImage({
      data: chart.dataUrl,
      x: SLIDE.margin + (boxW - w) / 2,
      y: boxTop + (boxH - h) / 2,
      w,
      h,
    });
  });
  await pptx.writeFile({ fileName: `${baseName}.pptx` });
};

const withHeader = (canvas, lines, scale) => {
  if (!lines?.length) return canvas;
  const lineH = HEADER_LINE_PX * scale;
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = canvas.height + Math.ceil(lineH * (lines.length + 0.5));
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.fillStyle = '#222222';
  ctx.textBaseline = 'top';
  lines.forEach((line, i) => {
    ctx.font = i === 0 ? `bold ${HEADER_FONT}` : HEADER_FONT;
    ctx.save();
    ctx.scale(scale, scale);
    ctx.fillText(line, 8, (i * lineH) / scale + 4);
    ctx.restore();
  });
  ctx.drawImage(canvas, 0, Math.ceil(lineH * (lines.length + 0.5)));
  return out;
};

const TRIM_CHUNK_ROWS = 64;

const isWhite = (data) => {
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return false;
  }
  return true;
};

// The report iframe is never shorter than its minimum height, so short
// reports end in blank space that would otherwise become empty PDF pages.
const trimBottom = (canvas) => {
  const ctx = canvas.getContext('2d');
  let bottom = canvas.height;
  while (bottom > 0) {
    const top = Math.max(0, bottom - TRIM_CHUNK_ROWS);
    if (!isWhite(ctx.getImageData(0, top, canvas.width, bottom - top).data)) break;
    bottom = top;
  }
  const height = Math.min(canvas.height, bottom + TRIM_CHUNK_ROWS);
  if (height <= 0 || height === canvas.height) return canvas;
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = height;
  out.getContext('2d').drawImage(canvas, 0, 0);
  return out;
};

const canvasToPdf = async (source, fileName) => {
  const canvas = trimBottom(source);
  const { jsPDF } = await import('jspdf');
  const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - 2 * PDF_MARGIN_PT;
  const pxPerPt = canvas.width / contentW;
  const slicePx = Math.floor((pageH - 2 * PDF_MARGIN_PT) * pxPerPt);
  for (let offset = 0, page = 0; offset < canvas.height; offset += slicePx, page += 1) {
    const h = Math.min(slicePx, canvas.height - offset);
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = h;
    slice.getContext('2d').drawImage(canvas, 0, offset, canvas.width, h, 0, 0, canvas.width, h);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', PDF_MARGIN_PT, PDF_MARGIN_PT, contentW, h / pxPerPt);
  }
  pdf.save(fileName);
};

/**
 * PDF of `element` as it is rendered now, preceded by `headerLines`.
 * `ignoreSelector` drops matching elements (toolbars, menus) from the
 * snapshot.
 */
export const exportElementPdf = async (element, baseName, { headerLines, ignoreSelector } = {}) => {
  const { default: html2canvas } = await import('html2canvas');
  const doc = element.ownerDocument;
  const fullHeight =
    element === doc.body ? doc.documentElement.scrollHeight : element.scrollHeight;
  const scale = Math.min(window.devicePixelRatio || 1, 2, MAX_CANVAS_HEIGHT / Math.max(1, fullHeight));
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale,
    useCORS: true,
    logging: false,
    windowWidth: doc.documentElement.scrollWidth,
    windowHeight: doc.documentElement.scrollHeight,
    ignoreElements: (el) => !!ignoreSelector && !!el.matches?.(ignoreSelector),
  });
  await canvasToPdf(withHeader(canvas, headerLines, scale), `${baseName}.pdf`);
};
